/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ShopperBasketsV2, ShopperProducts } from '@/scapi';
import type { AppConfig } from '@/types/config';
import { resolveProductImage } from '@/lib/product/resolved-image';
import { getAtpItemFields } from '@/lib/atp/atp-fields';
import { isReturnPliItem } from '@/lib/mattress-takeback/return-pli';
import { resolveFormattingLocale } from '@/lib/formatting-locale';

/**
 * Shared delivery-method grouping for the checkout shipping step (CHECKOUT-10)
 * and the order-review step (CHECKOUT-14).
 *
 * Single source of truth for the `c_deliveryMethod` bucketing so the
 * shipping-options view (`delivery-groups.tsx`) and the read-only review view
 * (`order-review-items.tsx`) stay in lock-step (same buckets, same order, same
 * labels). CHECKOUT-10 owns the data; this util only formalizes the display
 * grouping it already produced inline.
 */

/** Display order for the delivery sub-groups. Empty groups are skipped. */
export const DELIVERY_GROUP_ORDER = ['HD', 'DS', 'DS3PL'] as const;

export type DeliveryMethod = (typeof DELIVERY_GROUP_ORDER)[number];

/** Static map of group → i18n label key (avoids template-literal i18next type errors). */
export const DELIVERY_GROUP_LABEL_KEYS = {
    HD: 'shipping.group.hd',
    DS: 'shipping.group.ds',
    DS3PL: 'shipping.group.ds3pl',
} as const satisfies Record<DeliveryMethod, string>;

type ProductItem = ShopperBasketsV2.schemas['ProductItem'];
type Product = ShopperProducts.schemas['Product'];

/** Read a string custom attribute off a ProductItem without leaking `any`. */
export function getProductItemStringAttr(item: ProductItem, key: string): string | undefined {
    const value = (item as unknown as Record<string, unknown>)[key];
    return typeof value === 'string' ? value : undefined;
}

/**
 * True when the basket contains at least one line item whose effective
 * delivery method is Ashley Home Delivery (HD).
 *
 * Reads the `c_deliveryMethod` custom attribute stamped by
 * `sfnext_ashley_hooks/.../OrderDeliveryMethodHelper.resolveEffectivePliDeliveryMethod`
 * and mirrored onto every basket / order response by
 * `DeliveryMethodResponseStamper`. HD is a terminal bucket in that stamp — it
 * is never promoted from DS or DS3PL — so the raw string check is authoritative.
 *
 * The SSF-373 out-of-region gates on the storefront (cart banner + Proceed-to-
 * Checkout button + shipping-address pre-PUT short-circuit + checkout loader
 * OOR guard + multi-ship submit) all use this predicate to decide whether an
 * "unmapped" ZIP should block the shopper. HI/AK/PR ZIPs are unmapped in the
 * `ZipCodeMapping` / `hdShippingCost` custom-object tables by design — HD does
 * not ship there — but DS ground and DS3PL still do (via `standard-shipping-
 * ak-hi-pr`). A DS-only basket at 96779/99501 must therefore proceed. Mirrors
 * the HD-aware predicate the cartridge already uses to stamp
 * `basket.c_shippingUnavailable`
 * (`ShippingCostHelper.stampShippingUnavailableFlag`).
 *
 * A basket with no `productItems` (or a missing / non-string `c_deliveryMethod`
 * on every line) safely resolves to `false`. Kits, warranty PLIs, and other
 * non-HD lines are correctly excluded — HD is checked directly, not defaulted
 * to.
 */
export function basketHasHomeDeliveryItem(
    basket: { productItems?: readonly ProductItem[] | null } | null | undefined
): boolean {
    const items = basket?.productItems;
    if (!items || items.length === 0) return false;
    // SSF-648 — Skip the take-back `*RETURN` PLI. It is stamped with
    // `c_deliveryMethod='HD'` by design so it rides the HD shipment for
    // export purposes, but it is NOT a merchandise HD line — treating it as
    // one would keep the SSF-373 out-of-region storefront gates blocking a
    // DS-only basket whose shopper happened to opt into mattress take-back
    // (banner + Proceed-to-Checkout + shipping-address pre-PUT + checkout-
    // loader OOR guard + multi-ship submit). The server-side
    // `BasketResponseEnricher` already strips RETURN from most basket-GET
    // responses; this filter guarantees the predicate is correct even on
    // paths that bypass that hook (e.g. cookie-less SSO reconcile via
    // `getCustomerBaskets`).
    return items.some((item) => !isReturnPliItem(item) && getProductItemStringAttr(item, 'c_deliveryMethod') === 'HD');
}

/**
 * Formats a stored ISO date (e.g. `c_requestedDeliveryDatetime`) as
 * "Friday, April 6".
 *
 * The persisted `c_requestedDeliveryDatetime` is a `string` custom attribute
 * that round-trips as `yyyy-MM-dd` (see the shipment.afterPATCH format gate
 * in `sfnext_ashley_hooks`). We must NOT parse it via `new Date(value)` — the
 * ES spec interprets a date-only ISO string as UTC midnight, which
 * `Intl.DateTimeFormat` then renders in the user's local timezone. In any
 * negative-offset timezone (PST, MST, CST, EST, …) that yields the previous
 * calendar day (bug: user picks July 16, UI shows July 15). ATP `bestDate`
 * values arrive as full ISO datetimes (`yyyy-MM-ddTHH:mm:ss.sssZ`); we
 * strip the time component and parse the calendar date in local time.
 */
export function formatDeliveryDate(value: unknown, language: string): string | null {
    if (typeof value !== 'string' || value.length === 0) {
        return null;
    }
    const [datePart] = value.split('T');
    const [yearString, monthString, dayString] = datePart.split('-');
    const year = Number(yearString);
    const month = Number(monthString);
    const day = Number(dayString);
    if (!year || !month || !day || Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
        return null;
    }
    const parsed = new Date(year, month - 1, day, 12);
    if (parsed.getFullYear() !== year || parsed.getMonth() !== month - 1 || parsed.getDate() !== day) {
        return null;
    }
    return new Intl.DateTimeFormat(resolveFormattingLocale(language), {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
    }).format(parsed);
}

export interface ResolvedItemImage {
    /** DIS-optimized (when `config` is provided) thumbnail URL, or `undefined` when none resolves. */
    src: string | undefined;
    /** Alt text — catalog image alt, falling back to the product name. */
    alt: string;
}

/**
 * Resolve a basket line item's thumbnail for the checkout shipping / review steps.
 *
 * SCAPI basket `productItems` do NOT carry image URLs, so we join the item
 * against the checkout loader's `productMap` (keyed by `itemId` — the same map
 * `<MyCart/>` consumes) and reads the compact, variant-correct image added by
 * the product response hook.
 *
 * Returns `{ src: undefined }` when no product or image is found — callers
 * render their placeholder box in that case.
 */
export function resolveProductItemImage(
    item: ProductItem,
    productMap: Record<string, Product> | undefined,
    config?: AppConfig
): ResolvedItemImage {
    const fallbackAlt = item.productName || 'Product';
    const product = item.itemId ? productMap?.[item.itemId] : undefined;
    return resolveProductImage(product, fallbackAlt, config);
}

/**
 * Resolve a ProductItem's display delivery group, narrowed to a known bucket (or undefined).
 *
 * Legacy parity for the `DS3PL` case (SSF — Bedgear Basic Mattress Protector
 * regression, 2026-07-29; A600080400 storefront-display regression, 2026-07-30):
 *
 *   Legacy `org_ashcomm/COShipping.js:105-110` stamps
 *   `pli.custom.deliveryMethod = 'DS3PL'` on ANY DS product with
 *   `directShipThirdPartyFlag=true` — but the shopper-facing cart / checkout
 *   templates (`ds-shipping-cards.isml:186-204` + `deliverycardoption.isml:32-38`)
 *   render those items in the SAME "Ground Shipping / FREE" card as regular DS
 *   items UNLESS the item ALSO meets the fuller 3PBB predicate
 *   (`thirdPartyDistributionCenter && !expressShippingEligible &&
 *   directShipThirdPartyFlag` → legacy stamps `pli.custom.is3PBB=true`, and only
 *   THEN does the card switch to `delivery-msg-cart-whiteGlove-*` / curbside
 *   with a per-item fee).
 *
 *   In SFN the shipping-group UI labels the `DS3PL` bucket "White Glove
 *   Delivery" and stamps `qty × c_whiteGloveServiceFee` on the row — correct
 *   for true 3PBB items, wrong for a small-parcel 3P direct-ship item like
 *   the Bedgear mattress protector (`directShipThirdPartyFlag=true`,
 *   `expressShippingEligible=true` → is3PBB=false). Mirror the legacy
 *   fall-through: only items that satisfy the strict 4-flag 3PBB predicate
 *   stay in the `DS3PL` display bucket; the rest fold into `DS` so they
 *   render as "Standard Delivery / FREE".
 *
 *   The 3PBB signal is read via {@link getIs3PBB} which prefers `c_is3PBB`
 *   (stamped by `BasketEnrichmentHelper` on every basket GET using
 *   `AtpLineItemHelper.isProduct3PBB`, independent of ATP AGR success) and
 *   falls back to `c_atpIs3PBB` (the legacy ATP-side stamp) for rolling
 *   deploy safety. A missing / non-enriched flag safely defaults to the DS
 *   bucket — a "free ground shipping" fallback is a strictly-safer wrong
 *   answer than a phantom $99.99 White Glove card.
 */
export function getDeliveryMethod(item: ProductItem): DeliveryMethod | undefined {
    const method = getProductItemStringAttr(item, 'c_deliveryMethod');
    if (method === 'HD' || method === 'DS') return method;
    if (method === 'DS3PL') {
        return getIs3PBB(item) ? 'DS3PL' : 'DS';
    }
    return undefined;
}

/**
 * Read the strict-4-flag 3PBB storefront-display signal off a ProductItem.
 *
 * Primary source: `c_is3PBB` — stamped by `BasketEnrichmentHelper` on every
 * basket GET from `AtpLineItemHelper.isProduct3PBB(product, rawDeliveryMethod)`
 * (product attrs + `feature3PBBEnabled` site pref), independent of ATP AGR
 * success.
 *
 * Rolling-deploy fallback: `c_atpIs3PBB` — the legacy signal stamped by
 * `int_sfnext_atp` when a real ATP call resolves and the SKU passes the same
 * strict predicate. Kept so that during a deploy where the FE ships before the
 * cartridge (or a basket cached from before the cartridge deploy is returned
 * to a shopper on the new FE), real 3PBB items are still bucketed into the
 * White Glove card instead of silently regressing into Standard Delivery.
 * Either flag being `true` counts.
 */
function getIs3PBB(item: ProductItem): boolean {
    const raw = item as unknown as { c_is3PBB?: unknown; c_atpIs3PBB?: unknown };
    return raw.c_is3PBB === true || raw.c_atpIs3PBB === true;
}

export interface DeliveryBuckets {
    /** Items keyed by their delivery method. Unbucketed items (no/unknown method) are dropped. */
    buckets: Record<DeliveryMethod, ProductItem[]>;
    /** Non-empty groups in canonical HD → DS → DS3PL order. */
    visibleGroups: DeliveryMethod[];
}

/**
 * Bucket product items into HD / DS / DS3PL for STOREFRONT DISPLAY.
 *
 * Delegates the group decision to {@link getDeliveryMethod}, which already
 * applies the strict 4-flag 3PBB gate to the loose `c_deliveryMethod=DS3PL`
 * promotion — items that were promoted for BACKEND ROUTING but fail the
 * storefront-display predicate fall through into the DS bucket.
 *
 * Items without a recognized method are intentionally dropped (mirrors the
 * legacy SiteGenesis grouping, which only renders the three known buckets).
 * Callers are expected to pre-filter to a single shipment's items.
 */
export function bucketItemsByDeliveryMethod(items: ProductItem[]): DeliveryBuckets {
    const buckets: Record<DeliveryMethod, ProductItem[]> = { HD: [], DS: [], DS3PL: [] };
    for (const item of items) {
        const method = getDeliveryMethod(item);
        if (!method) continue;
        buckets[method].push(item);
    }
    const visibleGroups = DELIVERY_GROUP_ORDER.filter((group) => buckets[group].length > 0);
    return { buckets, visibleGroups };
}

/**
 * Shape accepted by `getGroupDeliveryDate` for the shipment argument. The
 * SCAPI-generated `ShopperBasketsV2.schemas['Shipment']` / `ShopperOrders.
 * schemas['Shipment']` types don't declare custom `c_*` attributes, so the
 * helper accepts `unknown` and reads `c_requestedDeliveryDatetime` via a
 * narrow cast internally — callers pass their shipment directly.
 */
export type ShipmentDeliveryDateSource = unknown;

/**
 * Resolve the "Get it by / Arriving …" date for a delivery bucket.
 *
 * The three groups have fundamentally different date sources — collapsing them
 * onto the shipment-level `c_requestedDeliveryDatetime` (as the UI historically
 * did) caused the DS group to visually inherit any date the shopper picked
 * for HD in the White Glove / Doorstep calendar (bug: "DS and 3PDS delivery
 * dates should not update to match the White Glove delivery date").
 *
 *  • HD (Ashley Home Delivery — White Glove / Doorstep) — shopper-selectable
 *    date sourced from the shipment-level `c_requestedDeliveryDatetime` the
 *    OHDS delivery-schedule action writes. This is the ONLY group whose date
 *    the shopper can change.
 *  • DS (Standard / Ground Shipping) — fulfiller-quoted date from ATP
 *    (`c_atpBestDate` on each line item). Ashley cannot modify this. When
 *    multiple items are in the bucket we return the *latest* bestDate so the
 *    single "Arriving …" line reflects when the whole bucket has arrived.
 *  • DS3PL (Premium / 3P Big-and-Bulky) — same treatment as DS: per-item ATP,
 *    aggregated by the latest bestDate. Note that 3PBB items sometimes bypass
 *    ATP entirely (`c_atpIs3PBB` true, `bestDate` null) — in that case there
 *    is no promised date and the row omits the "Arriving …" prefix.
 *
 * Returns the ISO `YYYY-MM-DD` date string, or `null` when no date can be
 * resolved (unpopulated HD schedule, non-enriched basket, all 3PBB items,
 * etc.). Callers format via `formatDeliveryDate`.
 *
 * Accepts either basket or order product items — both carry the ATP `c_atp*`
 * custom attributes with identical semantics, and the helper reads them
 * structurally (never touches basket-only fields).
 */
export function getGroupDeliveryDate(
    group: DeliveryMethod,
    items: readonly unknown[],
    shipment: ShipmentDeliveryDateSource
): string | null {
    if (group === 'HD') {
        const record = (shipment ?? {}) as Record<string, unknown>;
        const value = record.c_requestedDeliveryDatetime;
        return typeof value === 'string' && value.length > 0 ? value : null;
    }
    // DS / DS3PL — latest ATP bestDate across items in the bucket. Sorting
    // ISO YYYY-MM-DD lexicographically is chronologically correct.
    let latest: string | null = null;
    for (const item of items) {
        const { bestDate } = getAtpItemFields(item);
        if (bestDate && (latest === null || bestDate > latest)) {
            latest = bestDate;
        }
    }
    return latest;
}

/**
 * Row shape shared structurally by both `<DeliveryGroups/>`'s and
 * `<OrderReviewItems/>`'s local `DeliveryRow` unions (item vs. collapsed kit
 * row). Declared here — rather than importing either component's type —
 * so this module stays a leaf with no dependency on either consumer.
 */
type BackorderableRow = { kind: 'item'; item: ProductItem } | { kind: 'kit'; componentItems: ProductItem[] };

/**
 * Partition delivery rows for a DS / DS3PL group into "available" and
 * "backordered" buckets, kit-aware: a kit row is backordered if ANY
 * component is on backorder — matches the existing
 * {@link aggregateKitAtpFields} / `<BackorderNotice>` aggregate rule, so a
 * kit never shows as "available" while one of its components is delayed.
 *
 * Never called for HD — Ashley Home Delivery ships as a single scheduled
 * truck shipment regardless of any line's backorder status, so it always
 * renders as one section.
 *
 * Order is preserved within each output bucket (stable partition).
 */
export function splitDeliveryRowsByBackorderStatus<Row extends BackorderableRow>(
    rows: Row[]
): { available: Row[]; backordered: Row[] } {
    const available: Row[] = [];
    const backordered: Row[] = [];
    for (const row of rows) {
        const isBackordered =
            row.kind === 'item'
                ? getAtpItemFields(row.item).isOnBackOrder
                : row.componentItems.some((item) => getAtpItemFields(item).isOnBackOrder);
        (isBackordered ? backordered : available).push(row);
    }
    return { available, backordered };
}

/**
 * Flattens delivery rows back to their raw product items (kit rows expand to
 * their `componentItems`), in row order. Used to feed {@link getGroupDeliveryDate}
 * the correct item set for a split (available-only / backordered-only)
 * section, since that helper reads per-item `c_atp*` fields directly and has
 * no notion of collapsed kit rows.
 */
export function flattenDeliveryRowItems<Row extends BackorderableRow>(rows: Row[]): ProductItem[] {
    return rows.flatMap((row) => (row.kind === 'item' ? [row.item] : row.componentItems));
}
